import { baseApi } from '@/shared/api/baseApi';

import {
  paymentResultSchema,
  subscriptionSchema,
  type PaymentDetails,
  type PaymentResult,
  type Subscription,
} from './payments.schema';

interface AddCardArgs {
  /** Token from Stripe Elements — never raw card details, which must not reach
   *  this application at all. */
  cardToken: string;
  isDefault?: boolean;
}

interface PayAnnualReviewArgs extends PaymentDetails {
  annualReviewId: string;
}

interface PayInvoiceArgs extends PaymentDetails {
  invoiceId: string;
  /** ASIC invoices settle through a different route than agent invoices. */
  hasAsicInvoice: boolean;
}

/**
 * Card and payment endpoints.
 *
 * A successful charge invalidates the annual review and invoice caches: paid
 * status is what the director came to change, and leaving a stale "unpaid"
 * badge on screen after a payment is the one bug that generates support calls.
 */
export const paymentsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getSubscription: build.query<Subscription, undefined>({
      query: () => '/director/subscription',
      transformResponse: (response: unknown): Subscription =>
        subscriptionSchema.parse(response),
      providesTags: ['Subscription'],
    }),

    addCard: build.mutation<unknown, AddCardArgs>({
      query: ({ cardToken, isDefault = false }) => ({
        url: '/director/pay/card',
        method: 'POST',
        body: { sourceId: cardToken, isDefault },
      }),
      invalidatesTags: ['Subscription'],
    }),

    deleteCard: build.mutation<unknown, { cardId: string }>({
      query: ({ cardId }) => ({ url: `/director/pay/${cardId}`, method: 'DELETE' }),
      invalidatesTags: ['Subscription'],
    }),

    setDefaultCard: build.mutation<unknown, { cardId: string }>({
      query: ({ cardId }) => ({
        url: '/director/pay/default',
        method: 'PUT',
        body: { cardId },
      }),
      invalidatesTags: ['Subscription'],
    }),

    payAnnualReview: build.mutation<PaymentResult, PayAnnualReviewArgs>({
      query: ({ annualReviewId, ...body }) => ({
        url: `/director/pay/${annualReviewId}`,
        method: 'POST',
        body,
      }),
      // Parsed rather than trusted: a bank transfer branches on whether a
      // `client_secret` came back, and reading that off an unvalidated `any` is
      // how the legacy portal ended up confirming against `undefined`.
      transformResponse: (response: unknown): PaymentResult =>
        paymentResultSchema.parse(response),
      // `Document` covers the outstanding-items list, which is keyed on pay
      // status: without it a settled row keeps its "Unpaid" chip until reload.
      invalidatesTags: ['AnnualReview', 'Invoice', 'Document', 'Subscription'],
    }),

    payInvoice: build.mutation<PaymentResult, PayInvoiceArgs>({
      query: ({ invoiceId, hasAsicInvoice, ...body }) => ({
        url: hasAsicInvoice
          ? `/director/pay/invoice/asic/${invoiceId}`
          : `/director/pay/invoice/${invoiceId}`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown): PaymentResult =>
        paymentResultSchema.parse(response),
      invalidatesTags: ['Invoice', 'AnnualReview', 'Form', 'Document', 'Subscription'],
    }),
  }),
});

export const {
  useGetSubscriptionQuery,
  useAddCardMutation,
  useDeleteCardMutation,
  useSetDefaultCardMutation,
  usePayAnnualReviewMutation,
  usePayInvoiceMutation,
} = paymentsApi;
